import { useCallback, useEffect, useRef } from "react";
import { track } from "../lib/analytics";
import { nextLocalTick } from "../lib/local-tick";

// useBetRemoval (#1214 PR B, spec §16.5; pending adds #1554 Ultron G3).
// Single job: keep the client's own removals and additions authoritative over an in-flight cloud
// load for the session.
// Reads: user id (to reset, and to own pending adds). Writes: App's bets state through the setter it is given.
// Does NOT: call the server (delete-bet-client does), or guard the realtime hooks -- App passes
// guardedSetBets ONLY to useCloudSync, the one async writer that can add rows; the realtime
// hooks keep the raw setter so their channel dependencies never change (P1-1).
//
// Tombstones are String(id): rowToBet makes ids numbers, the route and imports use strings.
// guardedSetBets is referentially stable ([setBets]) and returns the SAME array reference when
// no tombstoned id is present, so a guarded write never forces a re-render by itself (P2-4).
// addBet clears the id's tombstone first: deterministic import ids mean a genuine re-import
// can reuse a removed id (P1-2). The set resets when user.id changes -- compared by id, not
// object, so a TOKEN_REFRESHED user keeps its tombstones (CC-IMPL-021).
//
// Pending adds (#1554 G3): a paged cloud load takes several requests, and its snapshot can predate
// a bet addBet just added (the import saved it on the server first, SlipImporter.jsx:648). addBet
// stamps the bet with a local tick; useCloudSync passes `{ loadTick }` (taken when its load
// started) with its value write. For each pending add, the write:
//   - contains the id           -> the server has it; the pending add is done;
//   - lacks it, add is NEWER    -> the load's snapshot predates the add; the bet stays, first;
//   - lacks it, add is OLDER    -> the load started after the add and the server does not have the
//                                  bet (deleted elsewhere); the pending add is dropped.
// A value write without loadTick (the sign-out []) keeps none. removeBet and a user change clear
// them. The kept bet is taken from CURRENT state, so a realtime patch on it is not lost.
// Each pending add is stamped with its owner (#1554 pre-Codex SEC-H): the user id of the render
// whose addBet ran, so an import started under A that lands after a switch to B (holding A's
// callback) is stamped A. A value write drops every entry not owned by the current user.

function withoutTombstoned(bets, tombstones) {
  if (tombstones.size === 0 || !Array.isArray(bets)) return bets;
  const hit = (b) => b != null && tombstones.has(String(b.id));
  return bets.some(hit) ? bets.filter((b) => !hit(b)) : bets;
}

// Settle every pending add against a cloud value write; returns the adds to keep, newest first.
// Mutates `pending` (drops the settled entries). Called outside any React updater.
// Exported for its unit test only: the owner check is a backstop behind addBet's guard, and the hook
// alone cannot reach it (#1554 Nova).
export function settlePendingAdds(bets, pending, loadTick, userId) {
  if (pending.size === 0 || !Array.isArray(bets)) return [];
  const loaded = new Set(bets.map((b) => (b == null ? "" : String(b.id))));
  const keep = [];
  for (const [key, entry] of pending) {
    if (entry.userId !== userId) pending.delete(key); // another user's add: never kept (SEC-H)
    else if (loaded.has(key)) pending.delete(key);
    else if (Number.isFinite(loadTick) && entry.tick > loadTick) keep.push(entry);
    else pending.delete(key);
  }
  return keep.reverse();
}

export function useBetRemoval({ user, setBets }) {
  const tombstonesRef = useRef(new Set());
  const pendingAddsRef = useRef(new Map());
  const userId = user?.id ?? null;
  const prevUserIdRef = useRef(userId);
  // The current user at settle time; guardedSetBets stays stable ([setBets]) so it reads a ref.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      tombstonesRef.current = new Set();
      pendingAddsRef.current = new Map();
    }
  }, [userId]);

  const guardedSetBets = useCallback((update, meta) => {
    if (typeof update === "function") {
      setBets((prev) => withoutTombstoned(update(prev), tombstonesRef.current));
      return;
    }
    const keep = settlePendingAdds(update, pendingAddsRef.current, meta?.loadTick, userIdRef.current);
    if (keep.length === 0) {
      setBets(withoutTombstoned(update, tombstonesRef.current));
      return;
    }
    setBets((prev) => {
      const current = new Map((Array.isArray(prev) ? prev : []).map((b) => [String(b?.id), b]));
      const kept = keep.map((entry) => current.get(String(entry.bet.id)) ?? entry.bet);
      return withoutTombstoned([...kept, ...update], tombstonesRef.current);
    });
  }, [setBets]);

  const removeBet = useCallback((id) => {
    const key = String(id);
    tombstonesRef.current.add(key);
    pendingAddsRef.current.delete(key);
    setBets((prev) => prev.filter((b) => String(b.id) !== key));
    track('slip_removed');
  }, [setBets]);

  const addBet = useCallback((bet) => {
    // A stale callback from an earlier user's render never touches this user's list (#1554 Nova).
    if (userId !== userIdRef.current) return;
    const key = String(bet.id);
    tombstonesRef.current.delete(key);
    pendingAddsRef.current.delete(key); // re-insert so Map order stays oldest -> newest
    // Owner from this render's closure, not a ref: a stale A callback must stamp A (SEC-H).
    pendingAddsRef.current.set(key, { bet, tick: nextLocalTick(), userId });
    setBets((prev) => [bet, ...prev]);
  }, [setBets, userId]);

  return { guardedSetBets, removeBet, addBet };
}
